import React, { useState, useContext } from "react";
import "../css/AfterHoursActionButtons.css";
import Modal from "../../../components/elements/Modal";
import RejectModal from "../../../components/elements/RejectModal";
import ApproveModal from "../../../components/elements/ApproveModal";
import SnackbarAlert from "../../../components/elements/SnackbarAlert";
import { useMutation } from "@tanstack/react-query";
import {
  TRIP_PHASE,
  ClearanceStatus as Status,
} from "../../../utils/constants";
import SuccessModal from "../../../components/elements/SuccessModal";
import { useFormData } from "../../../helper/FormDataProvider";
import { StorageContext } from "../../../services/StorageProvider";
import { getCurrentTimestamp } from "../../../utils/dateUtils";
import useLandingAuthApi from "../../../services/api/useLandingAuthApi";
import { airportCodeOnApprove, hasAirportAccess } from "../../../utils/Utils";

const AfterHoursActionButtons = ({
  tripId,
  officers,
  tripData,
  clearanceStatus: initialClearanceStatus,
  isArrival,
  isAutoApprove,
}) => {
  const { userData } = useFormData();
  const { airportData } = useContext(StorageContext);
  const { approveAfterHours, rejectAfterHours } = useLandingAuthApi();

  const [clearanceStatus, setClearanceStatus] = useState(
    initialClearanceStatus
  );
  const [modalState, setModalState] = useState({
    isApproveModalOpen: false,
    isRejectModalOpen: false,
  });
  const [selectedReason, setSelectedReason] = useState("");
  const [commonError, setCommonError] = useState(null);

  const [successModalData, setSuccessModalData] = useState({
    isOpen: false,
    title: "",
    message: "",
  });

  // Check if user has access to the relevant airport (departure or arrival)
  const targetAirportId = isArrival
    ? tripData?.arrivalAirport?.id
    : tripData?.departureAirport?.id;

  const hasAirportAccessForAction = hasAirportAccess(
    userData,
    airportData,
    targetAirportId
  );

  const closeSuccessModal = () => {
    setSuccessModalData({
      isOpen: false,
      title: "",
      message: "",
    });
  };
  const afterHoursDepartureData = {
    overtimeOfficers: officers,
    tripPhase: TRIP_PHASE.DEPARTURE,
  };
  const afterHoursArrivalData = {
    overtimeOfficers: officers,
    tripPhase: TRIP_PHASE.ARRIVAL,
  };

  const isApproved =
    clearanceStatus === Status.CLEARANCE_GRANTED ||
    clearanceStatus === Status.PAID;
  const isRejected = clearanceStatus === Status.CLEARANCE_REJECTED;
  const isCancelled = clearanceStatus === Status.CANCELLED;
  const allDisabled = isApproved || isRejected || isCancelled || isAutoApprove || !hasAirportAccessForAction;

  const openModal = (modalName) => {
    if (modalName === "isApproveModalOpen") {
      if (officers.length < 2) {
        setCommonError("At least 2 officers must be added before approving");
        setTimeout(() => setCommonError(null), 3000);
        return;
      }

      // Check for duplicate officers
      const uniqueOfficerIds = new Set(
        officers.map((officer) => officer.officerId)
      );
      if (uniqueOfficerIds.size !== officers.length) {
        setCommonError("Cannot have duplicate officers");
        setTimeout(() => setCommonError(null), 3000);
        return;
      }
    }

    setModalState({
      isApproveModalOpen: false,
      isRejectModalOpen: false,
      [modalName]: true,
    });
    setCommonError(null);
  };

  const closeModal = (modalName) => {
    setModalState((prevState) => ({
      ...prevState,
      [modalName]: false,
    }));
  };

  const { mutate: approveAfterHoursMutate, isPending: isApprovePending } =
    useMutation({
      mutationFn: ({ tripId, data }) => approveAfterHours(tripId, data),
      onSuccess: () => {
        setClearanceStatus(Status.CLEARANCE_GRANTED);

        setSuccessModalData({
          isOpen: true,
          title: "Approved",
          message: "You have successfully approved the After Hour Request",
        });
        closeModal("isApproveModalOpen");
      },
      onError: (error) => {
        setCommonError(error.message);
        closeModal("isApproveModalOpen");
      },
    });

  const handleApproveAfterHours = () => {
    if (isArrival) {
      approveAfterHoursMutate({ tripId, data: afterHoursArrivalData });
    } else {
      approveAfterHoursMutate({ tripId, data: afterHoursDepartureData });
    }
  };

  const { mutate: rejectAfterHoursMutate, isPending: isRejectPending } =
    useMutation({
      mutationFn: ({ tripId, data }) => rejectAfterHours(tripId, data),
      onSuccess: () => {
        setClearanceStatus(Status.CLEARANCE_REJECTED);
        setSuccessModalData({
          isOpen: true,
          title: "Rejected",
          message: "The request was rejected for this Trip",
        });
        closeModal("isRejectModalOpen");
      },
      onError: (error) => {
        setCommonError(error.message);
        closeModal("isRejectModalOpen");
      },
    });

  const handleRejectAfterHours = (e) => {
    e.preventDefault();
    rejectAfterHoursMutate({
      tripId,
      data: {
        rejectionReason: selectedReason,
        tripPhase: isArrival ? TRIP_PHASE.ARRIVAL : TRIP_PHASE.DEPARTURE,
      },
    });
  };

  const getButtonProps = (type) => {
    if (type === "approve") {
      return {
        text: isApproved ? "Approved" : "Approve",
        className: `after-hours-details-btn ${isApproved
          ? "approved not-clickable"
          : "after-hours-details-approve-btn"
          }`,
        onClick: isApproved ? null : () => openModal("isApproveModalOpen"),
        disabled: allDisabled && !isApproved,
      };
    } else if (type === "reject") {
      return {
        text: isRejected ? "Rejected" : "Reject",
        className: `after-hours-details-btn ${isRejected
          ? "rejected not-clickable"
          : "after-hours-details-rejection-btn"
          }`,
        onClick: isRejected ? null : () => openModal("isRejectModalOpen"),
        disabled: allDisabled && !isRejected,
      };
    }
    return {};
  };
  const airportCode = isArrival
    ? airportCodeOnApprove(
      tripData?.arrivalAirport.iataCode,
      tripData?.arrivalAirport.icaoCode
    )
    : airportCodeOnApprove(
      tripData?.departureAirport.iataCode,
      tripData?.departureAirport.icaoCode
    );
  return (
    <div>
      {commonError && <SnackbarAlert type="error" data-testid="snackbar-alert">{commonError}</SnackbarAlert>}

      {!hasAirportAccessForAction && (
        <div className="action-buttons-message">
          <span className="info-icon">ℹ️</span>
          You don't have access to the {isArrival ? 'arrival' : 'departure'} airport, so you're not authorized to Approve or Reject this After Hours request.
        </div>
      )}

      <div className="after-hours-details-action-buttons" >
        <button {...getButtonProps("approve")} data-testid="approve-button">
          {getButtonProps("approve").text}
        </button>
        <button {...getButtonProps("reject")} data-testid="after-hours-reject-button">
          {getButtonProps("reject").text}
        </button>
      </div>

      <Modal
        isOpen={modalState.isRejectModalOpen}
        onClose={() => closeModal("isRejectModalOpen")}
      >
        <RejectModal
          selectedReason={selectedReason}
          onReasonChange={(e) => setSelectedReason(e.target.value)}
          isLoading={isRejectPending}
          onRejectClick={handleRejectAfterHours}
        />
      </Modal>

      <Modal
        isOpen={modalState.isApproveModalOpen}
        onClose={() => closeModal("isApproveModalOpen")}
      >
        <ApproveModal
          email={userData?.email}
          tripId={tripData?.trackingId}
          airport={airportCode}
          timestamp={getCurrentTimestamp()}
          onApprove={handleApproveAfterHours}
          isLoading={isApprovePending}
          message={"Do you want to approve the After Hour fee?"}
        />
      </Modal>
      <SuccessModal
        isOpen={successModalData.isOpen}
        onClose={closeSuccessModal}
        title={successModalData.title}
        message={successModalData.message}
      />
    </div>
  );
};

export default AfterHoursActionButtons;
